import { apiSlice } from "fond/api/apiSlice";
import { GeoreferencedImageItem, ListCustomOverlaysResponse, ListGeoreferencedImagesResponse } from "fond/types/overlays";

/**
 * IndexedDB helpers for georeferenced images.
 * Temporary until backend support is available.
 * Uses IndexedDB instead of localStorage to avoid quota issues with large data URLs.
 */
const DB_NAME = "fond-georeferenced-images";
const DB_VERSION = 1;
const STORE_NAME = "images";

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function readGeoreferencedImages(projectId: string): Promise<GeoreferencedImageItem[]> {
  try {
    const db = await openDb();
    return new Promise((resolve) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const request = store.get(projectId);
      request.onsuccess = () => resolve(request.result ?? []);
      request.onerror = () => resolve([]);
    });
  } catch {
    return [];
  }
}

async function writeGeoreferencedImages(projectId: string, images: GeoreferencedImageItem[]): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(images, projectId);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Custom Overlays API Slice
 */
export const overlaysSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    getOverlays: build.query<ListCustomOverlaysResponse, { versionId: string } | { multiProjectId: string }>({
      query: (params) => ({
        url: `/v2/custom-overlays`,
        method: "GET",
        params: "versionId" in params ? { version_id: params.versionId } : { multi_project_id: params.multiProjectId },
      }),
      providesTags: (_result, _error, params) => {
        const id = "versionId" in params ? params.versionId : params.multiProjectId;
        return [{ type: "Overlay", id }];
      },
    }),
    createOverlay: build.mutation<void, { name: string; sourceVersionId: string; targetType: "VERSION" | "MULTI_PROJECT"; targetId: string }>({
      query: ({ name, sourceVersionId, targetType, targetId }) => ({
        url: `/v2/custom-overlays`,
        method: "POST",
        body: {
          name,
          source_version: {
            id: sourceVersionId,
          },
          target: {
            type: targetType === "VERSION" ? "version" : "multi_project",
            id: targetId,
          },
        },
      }),
      invalidatesTags: (_result, _error, { targetId }) => {
        return [{ type: "Overlay", id: targetId }];
      },
    }),
    updateOverlay: build.mutation<void, { overlayId: string; name: string; versionId?: string; multiProjectId?: string }>({
      query: ({ overlayId, name }) => ({
        url: `/v2/custom-overlays/${overlayId}`,
        method: "PATCH",
        body: { Name: name },
      }),
      invalidatesTags: (_result, _error, { versionId, multiProjectId }) => {
        const id = versionId || multiProjectId;
        return [{ type: "Overlay", id }];
      },
    }),
    deleteOverlay: build.mutation<void, { overlayId: string } & ({ versionId: string } | { multiProjectId: string })>({
      query: ({ overlayId }) => ({
        url: `/v2/custom-overlays/${overlayId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, params) => {
        const id = "versionId" in params ? params.versionId : params.multiProjectId;
        return [{ type: "Overlay", id }];
      },
    }),

    /**
     * Georeferenced Images — IndexedDB-backed endpoints.
     * These simulate real API endpoints and will be replaced with actual
     * backend calls once the API is available.
     */
    getGeoreferencedImages: build.query<ListGeoreferencedImagesResponse, { projectId: string }>({
      queryFn: async ({ projectId }) => {
        const images = await readGeoreferencedImages(projectId);
        return { data: { GeoreferencedImages: images } };
      },
      providesTags: (_result, _error, { projectId }) => [{ type: "GeoreferencedImage", id: projectId }],
    }),
    createGeoreferencedImage: build.mutation<
      GeoreferencedImageItem,
      { projectId: string; name: string; imageUrl: string; imageCorners: GeoreferencedImageItem["ImageCorners"]; opacity: number }
    >({
      queryFn: async ({ projectId, name, imageUrl, imageCorners, opacity }) => {
        const images = await readGeoreferencedImages(projectId);
        const newImage: GeoreferencedImageItem = {
          ID: crypto.randomUUID(),
          Name: name,
          ImageUrl: imageUrl,
          ImageCorners: imageCorners,
          Opacity: opacity,
        };
        images.push(newImage);
        await writeGeoreferencedImages(projectId, images);
        return { data: newImage };
      },
      invalidatesTags: (_result, _error, { projectId }) => [{ type: "GeoreferencedImage", id: projectId }],
    }),
    updateGeoreferencedImage: build.mutation<
      GeoreferencedImageItem,
      { projectId: string; imageId: string } & Partial<Omit<GeoreferencedImageItem, "ID">>
    >({
      queryFn: async ({ projectId, imageId, ...updates }) => {
        const images = await readGeoreferencedImages(projectId);
        const index = images.findIndex((img) => img.ID === imageId);
        if (index === -1) {
          return { error: { status: 404, data: "Georeferenced image not found" } };
        }
        images[index] = { ...images[index], ...updates };
        await writeGeoreferencedImages(projectId, images);
        return { data: images[index] };
      },
      invalidatesTags: (_result, _error, { projectId }) => [{ type: "GeoreferencedImage", id: projectId }],
    }),
    deleteGeoreferencedImage: build.mutation<void, { projectId: string; imageId: string }>({
      queryFn: async ({ projectId, imageId }) => {
        const images = await readGeoreferencedImages(projectId);
        const filtered = images.filter((img) => img.ID !== imageId);
        await writeGeoreferencedImages(projectId, filtered);
        return { data: undefined };
      },
      invalidatesTags: (_result, _error, { projectId }) => [{ type: "GeoreferencedImage", id: projectId }],
    }),
  }),
});

/**
 * Endpoint Hooks
 */
export const {
  useGetOverlaysQuery,
  useCreateOverlayMutation,
  useUpdateOverlayMutation,
  useDeleteOverlayMutation,
  useGetGeoreferencedImagesQuery,
  useCreateGeoreferencedImageMutation,
  useUpdateGeoreferencedImageMutation,
  useDeleteGeoreferencedImageMutation,
} = overlaysSlice;
